import type { FirestoreTimestamp } from './timestamp'
import type { PartialRange } from './social'

/**
 * An artist the user follows, app-wide. Not "interested at this event" — that's
 * `EventArtistInterest`. `createdAt` is written only by the Admin SDK: client
 * follows carry exactly `{ userId, artistId }`, so their creation time is the
 * snapshot's `createTime`.
 */
export interface ArtistInterest {
  id: string
  userId: string
  artistId: string
  createdAt: FirestoreTimestamp
  /**
   * Set by `recapArtistInterests` when the artist was added because the user
   * marked one of their sets `going` at `originEventId`. Absent = hearted by hand.
   */
  origin?: 'festival_recap'
  originEventId?: string
}

export type InterestStatus = 'interested' | 'going'

/**
 * Event RSVPs carry a third answer sets don't: a deliberate "no". It's stored
 * rather than inferred from a missing doc so friends can see you've decided,
 * and so a "not going" can't be mistaken for never having looked.
 */
export type EventAttendanceStatus = InterestStatus | 'not_going'

/**
 * Normalizes a raw stored RSVP. Docs written before `status` existed carry no
 * value and read as `interested`, so every reader has to agree on that default.
 */
export function attendanceStatus(raw: unknown): EventAttendanceStatus {
  return raw === 'going' || raw === 'not_going' ? raw : 'interested'
}

/**
 * Whether an RSVP means "this is in my plans". `not_going` is a decision
 * against, never a weaker `interested` — anything that lists, counts, notifies
 * or seeds off RSVPs must filter through this rather than testing
 * `status === 'going'` and letting the else branch mean interested.
 *
 * When the filter can happen in the query, prefer `where('status', 'in',
 * ATTENDING_STATUSES)` — but note it also drops pre-status docs, so only use it
 * where those don't matter.
 */
export function isAttending(raw: unknown): boolean {
  return attendanceStatus(raw) !== 'not_going'
}

export const ATTENDING_STATUSES: InterestStatus[] = ['interested', 'going']

/**
 * An artist hearted on one event's lineup — what "interested" means there, so
 * every event starts clean. `ArtistInterest` stays the app-wide follow list: a
 * heart writes both, an un-heart deletes only this.
 * Id: `eventArtistInterestId(userId, eventId, artistId)`.
 */
export interface EventArtistInterest {
  id: string
  userId: string
  eventId: string
  artistId: string
  createdAt: FirestoreTimestamp
  /**
   * 'backfill' = restored from a follow by scripts/backfill-event-artist-interests.ts
   * (Admin SDK); seedSetsOnEventArtistInterest skips those. Absent = hearted in the app or via MCP.
   */
  origin?: 'backfill'
}

export interface SetInterest {
  id: string
  userId: string
  eventId: string
  setId: string
  status?: InterestStatus
  /**
   * Minutes from the set's start bounding when the user will actually be there.
   * Omitted means the whole set, so every pre-existing doc reads correctly.
   * Only meaningful on a `going` interest — setting a range promotes the doc.
   */
  partialRange?: PartialRange
  createdAt: FirestoreTimestamp
}

/**
 * "I'm here" — the set a user confirmed they're actually at, as opposed to the
 * sets they planned (`SetInterest`). One doc per user per event, id
 * `${userId}_${eventId}`, so checking in elsewhere overwrites it and nobody is
 * at two sets at once. Readers treat it as active only while the set is playing
 * inside the user's own window (partial range) and they still have it marked —
 * `expiresAt` is the set's end, kept for TTL cleanup rather than for reads.
 */
/**
 * "Where are you right now?" — one doc per user per event (`${userId}_${eventId}`),
 * overwritten by every answer, so a set check-in and a live status are mutually
 * exclusive by construction. Two shapes:
 * - at a set: `setId`; activeness derives from the set + the user's range (`isCheckInActive`);
 * - a live status ("Getting food"): `status`, `stageId?`, `setAt`; active while
 *   setAt ≤ now < expiresAt (`isLiveStatusActive`, shared/liveStatus.ts). setAt/expiresAt
 *   use the client's preview-aware clock. Clients older than live statuses ignore these
 *   docs (no `setId` → no set to be at).
 */
export interface CheckIn {
  id: string
  userId: string
  eventId: string
  setId?: string
  /** A LIVE_STATUS_IDS value; readers ignore unknown ids. */
  status?: string
  /** The stage they're near (or, for `heading`, walking to). */
  stageId?: string
  setAt?: FirestoreTimestamp
  expiresAt: FirestoreTimestamp
  checkedInAt: FirestoreTimestamp
}

/** A check-in doc in its live-status shape. */
export type LiveStatus = CheckIn & { status: string; setAt: FirestoreTimestamp }

export interface EventInterest {
  id: string
  userId: string
  eventId: string
  status?: EventAttendanceStatus
  createdAt: FirestoreTimestamp
  /** When `status` was last set (server time). Absent on docs written before the Crew activity feed. */
  updatedAt?: FirestoreTimestamp
}

// Deterministic interest doc ids, so two writers racing to create the same
// interest land on one doc. Older docs carry auto ids — always look interests
// up by query, never by computing the id.
export function eventInterestId(userId: string, eventId: string): string {
  return `${userId}_${eventId}`
}

export function setInterestId(userId: string, setId: string): string {
  return `${userId}_${setId}`
}

export function eventArtistInterestId(userId: string, eventId: string, artistId: string): string {
  return `${userId}_${eventId}_${artistId}`
}
